import templates from '../templates/index.js';

import {
  getSpokenDate,
  formatLargeNumber,
  toTitleCase,
  compileTemplate,
} from '../helpers/utils.js';

/**
 * The AdaptiveCard class is used to build out an object that can
 * be converted into a JSON template for use with Microsoft's teams endpoint
 * This was rewritten in JS using the Portal Python endpoint as a guide.
 */
export class AdaptiveCard {
  /**
   * @property {Array} cardHeader - An empty array copied over from previous code.
   *   Enables future modification of header based on card type.
   * @property {Object[]} [cardBody=templates.title] - Template objects.
   * @property {Object[]} [cardFooter=templates.footer] - Footer template objects.
   * @property {boolean} cardDataGenerated - Internal "state" property
   * @property {Object} cardFormatVariables - An object containing any variables
   * and their values for replacement in the final JSON string.
   */
  constructor() {
    this.cardHeader = []; // default to null in prior portal code
    this.cardBody = [...templates.title];
    this.cardFooter = templates.footer;

    this.cardDataGenerated = false;
    this.cardFormatVariables = {};
  }

  /**
   * @typedef {Object} AdaptiveCard~_defaultCardData
   * @property {string} $schema - Reference to MS Adaptive Card json schema.
   * @property {string} $version - The Adaptive Card schema version
   * @property {string} type - Schema type
   */
  /**
   * @type {AdaptiveCard~_defaultCardData}
   * @static
   */
  _defaultCardData = {
    $schema: 'http://adaptivecards.io/schemas/adaptive-card.json',
    version: '1.6',
    type: 'AdaptiveCard',
  };

  /**
   * Normalize fields on incoming card data, specifically the data_points field
   * @param {Object} data - The object's data passed-in, or parsed from JSON
   * @returns {Object}
   * @private
   */
  // eslint-disable-next-line class-methods-use-this
  _normalizeData = data => {
    const { object_properties: objectProperties } = data;
    let { data_points: dataPoints, ...obj } = objectProperties
      ? { ...objectProperties }
      : data;

    if (typeof dataPoints === 'object' && !Array.isArray(dataPoints)) {
      dataPoints = [dataPoints];
    }

    for (const point of dataPoints) {
      obj = { ...obj, ...point };
    }

    return obj;
  };

  /**
   * Add a given template to the card body.
   * @param {Object} template - The specific template to add
   * @returns {undefined}
   */
  addToCardBody(template) {
    this.cardBody = Array.isArray(template)
      ? [...this.cardBody, ...template]
      : [...this.cardBody, template];
  }

  /**
   * Build out the card
   * @param {Object|string} data - The object data that serves as the basis
   * for the template generation.
   * @returns {string} JSON version returned in case a single-call is desired.
   */
  buildCard(data) {
    /* eslint-disable camelcase */
    const { header_template, body_template, footer_template } = data;

    if (header_template) this.cardHeader = header_template;
    if (footer_template) this.cardFooter = footer_template;

    const obj = this._normalizeData(
      typeof data === 'object' ? data : JSON.parse(data)
    );

    const {
      name,
      url,
      image_url: imageUrl,
      card_message: cardMessage,
      id_count: idCount,
      hh_count: hhCount,
      device_count: deviceCount,

      // campaign-specific
      campaign_step_data: campaignStepData,
    } = obj;

    const { duration, step } = campaignStepData || {};

    const cardFormatVariables = {
      createWhen: getSpokenDate(obj.create_date),
      objectType: toTitleCase(obj.type),
      objectName: name,
      objectUrl: url,
    };

    if (imageUrl) {
      this.addToCardBody(templates.bigImageBlock);
      cardFormatVariables.imageUrl = imageUrl;
    }

    if (cardMessage) {
      this.addToCardBody(templates.cardMessage);
      cardFormatVariables.cardMessage = cardMessage;
    }

    /**
     * If id_count is present, hh_count and device_count will be as well
     * they exist in the id-count.js template. Any of them may === 0.
     */
    if (idCount !== null && idCount !== undefined) {
      this.addToCardBody(templates.idCounts);

      cardFormatVariables.formattedIdCount = formatLargeNumber(idCount);
      cardFormatVariables.formattedHhCount = formatLargeNumber(hhCount);
      cardFormatVariables.formattedDeviceCount = formatLargeNumber(deviceCount);
    }

    if (campaignStepData) {
      this.addToCardBody(templates.campaignStepData);

      cardFormatVariables.formattedDuration = duration;
      cardFormatVariables.formattedStep = step;
    }

    this.cardFormatVariables = cardFormatVariables;
    this.cardDataGenerated = true;

    if (body_template) this.cardBody = body_template;

    return this.getCardJson();
    /* eslint-enable camelcase */
  }

  /**
   * Returns a JSON-stringified version of the card data object, which is passed
   * to {@link utils.compileTemplate} for the interpolation of the
   * handlebars-style replacements.
   * @throws {Error} - If card data has not been generated.
   */
  getCardJson() {
    if (!this.cardDataGenerated)
      throw new Error(
        'Card data has not been generated. See AdaptiveCardBuild.buildCard static method.'
      );

    return compileTemplate(
      JSON.stringify(
        {
          ...this._defaultCardData,
          body: [...this.cardHeader, ...this.cardBody, ...this.cardFooter],
        },
        undefined,
        2
      ),
      this.cardFormatVariables
    );
  }
}
